import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { 
  Building2, Users, FolderKanban, Plus, Search, ChevronRight, Settings, UserPlus, 
  Briefcase, Clock, DollarSign, Trash2, History, CheckCircle2, PauseCircle, 
  PlayCircle, CircleDashed, ChevronDown, MessageSquare, Archive, ArchiveRestore, Loader2
} from "lucide-react";

// Project status configuration
const PROJECT_STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "bg-gray-100 text-gray-700", icon: CircleDashed },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700", icon: PlayCircle },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  on_hold: { label: "On Hold", color: "bg-amber-100 text-amber-700", icon: PauseCircle },
};

const AdminCompanies = () => {
  const { token, API, user, isSuperAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [permissionLevels, setPermissionLevels] = useState({});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectStatusHistory, setProjectStatusHistory] = useState([]);
  const [showProjectDetailDialog, setShowProjectDetailDialog] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(null);

  // New company form
  const [newCompany, setNewCompany] = useState({
    name: "",
    industry: "",
    website: "",
    phone: "",
    address: "",
    notes: "",
  });

  useEffect(() => {
    fetchCompanies();
    fetchPermissionLevels();
    fetchAvailableUsers();
  }, [showArchived]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const params = showArchived ? "?include_archived=true" : "";
      const response = await fetch(`${API}/companies/${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveToggle = async (company) => {
    setArchiving(company.company_id);
    try {
      const response = await fetch(`${API}/companies/${company.company_id}/archive`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchCompanies();
        if (showDetailDialog && companyDetail?.company?.company_id === company.company_id) {
          fetchCompanyDetail(company.company_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to archive company");
      }
    } catch (error) {
      toast.error("Failed to archive company");
    } finally {
      setArchiving(null);
    }
  };

  const fetchPermissionLevels = async () => {
    try {
      const response = await fetch(`${API}/companies/permission-levels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPermissionLevels(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users?role=client`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchCompanyDetail = async (companyId) => {
    try {
      const response = await fetch(`${API}/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCompanyDetail(data);
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error("Failed to load company details");
    }
  };

  const createCompany = async () => {
    if (!newCompany.name.trim()) {
      toast.error("Company name is required");
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append("name", newCompany.name);
      if (newCompany.industry) params.append("industry", newCompany.industry);
      if (newCompany.website) params.append("website", newCompany.website);
      if (newCompany.phone) params.append("phone", newCompany.phone);
      if (newCompany.address) params.append("address", newCompany.address);
      if (newCompany.notes) params.append("notes", newCompany.notes);

      const response = await fetch(`${API}/companies/?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Company created successfully");
        setShowCreateDialog(false);
        setNewCompany({ name: "", industry: "", website: "", phone: "", address: "", notes: "" });
        fetchCompanies();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create company");
      }
    } catch (error) {
      toast.error("Failed to create company");
    }
  };

  const addUserToCompany = async (companyId, userId, permissionLevel) => {
    try {
      const params = new URLSearchParams();
      params.append("user_id", userId);
      params.append("permission_level", permissionLevel);

      const response = await fetch(`${API}/companies/${companyId}/users?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("User added to company");
        fetchCompanyDetail(companyId);
        fetchCompanies();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add user");
      }
    } catch (error) {
      toast.error("Failed to add user");
    }
  };

  const updateUserPermissions = async (companyId, userId, permissionLevel) => {
    try {
      const params = new URLSearchParams();
      params.append("permission_level", permissionLevel);

      const response = await fetch(`${API}/companies/${companyId}/users/${userId}?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Permissions updated");
        fetchCompanyDetail(companyId);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update permissions");
      }
    } catch (error) {
      toast.error("Failed to update permissions");
    }
  };

  const removeUserFromCompany = async (companyId, userId) => {
    if (!window.confirm("Remove this user from the company?")) return;

    try {
      const response = await fetch(`${API}/companies/${companyId}/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("User removed from company");
        fetchCompanyDetail(companyId);
        fetchCompanies();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to remove user");
      }
    } catch (error) {
      toast.error("Failed to remove user");
    }
  };

  const createProjectForCompany = async (companyId, projectData) => {
    try {
      const params = new URLSearchParams();
      params.append("name", projectData.name);
      if (projectData.description) params.append("description", projectData.description);
      params.append("monthly_hours", projectData.monthly_hours || "40");
      params.append("hourly_rate", projectData.hourly_rate || "150");
      if (projectData.assigned_users) params.append("assigned_users", projectData.assigned_users);

      const response = await fetch(`${API}/admin/companies/${companyId}/projects?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Project created successfully");
        fetchCompanyDetail(companyId);
        fetchCompanies();
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create project");
        return false;
      }
    } catch (error) {
      toast.error("Failed to create project");
      return false;
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm("Delete this project? This action cannot be undone.")) return;

    try {
      const response = await fetch(`${API}/admin/projects/${projectId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Project deleted");
        if (companyDetail) {
          fetchCompanyDetail(companyDetail.company.company_id);
        }
        fetchCompanies();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete project");
      }
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const openProjectDetail = async (project) => {
    setSelectedProject(project);
    setShowProjectDetailDialog(true);
    await fetchProjectStatusHistory(project.project_id);
  };

  const fetchProjectStatusHistory = async (projectId) => {
    try {
      const response = await fetch(`${API}/admin/projects/${projectId}/status-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProjectStatusHistory(data.history || []);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const updateProjectStatus = async (projectId, newStatus, note = "") => {
    try {
      const params = new URLSearchParams();
      params.append("new_status", newStatus);
      if (note) params.append("note", note);

      const response = await fetch(`${API}/admin/projects/${projectId}/status?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Project status updated");
        // Refresh data
        if (companyDetail) {
          fetchCompanyDetail(companyDetail.company.company_id);
        }
        if (selectedProject) {
          setSelectedProject({ ...selectedProject, project_status: newStatus });
          fetchProjectStatusHistory(projectId);
        }
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update status");
        return false;
      }
    } catch (error) {
      toast.error("Failed to update status");
      return false;
    }
  };

  const updateProjectAssignedUsers = async (projectId, userIds) => {
    try {
      const response = await fetch(
        `${API}/admin/projects/${projectId}/assigned-users?user_ids=${userIds.join(",")}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success("Assigned users updated");
        // Refresh data
        if (companyDetail) {
          fetchCompanyDetail(companyDetail.company.company_id);
        }
        if (selectedProject) {
          setSelectedProject({ ...selectedProject, assigned_users: userIds });
        }
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update assigned users");
        return false;
      }
    } catch (error) {
      toast.error("Failed to update assigned users");
      return false;
    }
  };

  const runMigration = async () => {
    if (!window.confirm("This will create companies from existing clients. Continue?")) return;

    try {
      const response = await fetch(`${API}/companies/migrate-clients`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Migration complete: ${result.companies_created} companies created, ${result.users_linked} users linked`);
        fetchCompanies();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Migration failed");
      }
    } catch (error) {
      toast.error("Migration failed");
    }
  };

  const filteredCompanies = companies.filter((company) => {
    const matchesSearch =
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.industry?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || company.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusColors = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-gray-100 text-gray-800",
      archived: "bg-red-100 text-red-800",
    };
    return (
      <Badge className={statusColors[status] || "bg-gray-100"}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A2636]">Companies</h1>
            <p className="text-gray-600">Manage client organizations and user access</p>
          </div>
          <div className="flex gap-2">
            {isSuperAdmin && (
              <Button variant="outline" onClick={runMigration} data-testid="migrate-btn">
                <Settings className="w-4 h-4 mr-2" />
                Migrate Clients
              </Button>
            )}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#2471A3] hover:bg-[#1A5276]" data-testid="create-company-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Company
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Company</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Company Name *</label>
                    <Input
                      value={newCompany.name}
                      onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                      placeholder="Acme Healthcare"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Industry</label>
                    <Input
                      value={newCompany.industry}
                      onChange={(e) => setNewCompany({ ...newCompany, industry: e.target.value })}
                      placeholder="Healthcare"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Website</label>
                    <Input
                      value={newCompany.website}
                      onChange={(e) => setNewCompany({ ...newCompany, website: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Phone</label>
                    <Input
                      value={newCompany.phone}
                      onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Address</label>
                    <Input
                      value={newCompany.address}
                      onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                      placeholder="123 Main St, City, State"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Notes</label>
                    <Input
                      value={newCompany.notes}
                      onChange={(e) => setNewCompany({ ...newCompany, notes: e.target.value })}
                      placeholder="Internal notes..."
                    />
                  </div>
                  <Button
                    onClick={createCompany}
                    className="w-full bg-[#2471A3] hover:bg-[#1A5276]"
                    data-testid="submit-company-btn"
                  >
                    Create Company
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-companies"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={showArchived ? "default" : "outline"}
                onClick={() => setShowArchived(!showArchived)}
                className={showArchived ? "bg-amber-500 hover:bg-amber-600" : ""}
                data-testid="show-archived-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                {showArchived ? "Showing Archived" : "Show Archived"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Companies Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2471A3]"></div>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
              <p className="text-gray-500 mb-4">
                {companies.length === 0
                  ? "Get started by creating your first company or migrating existing clients."
                  : "No companies match your search criteria."}
              </p>
              {companies.length === 0 && (
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={runMigration}>
                    Migrate Existing Clients
                  </Button>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    className="bg-[#2471A3] hover:bg-[#1A5276]"
                  >
                    Create Company
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredCompanies.map((company) => (
              <Card
                key={company.company_id}
                className={`hover:shadow-md transition-shadow cursor-pointer ${company.is_archived ? 'opacity-70 bg-gray-50' : ''}`}
                onClick={() => fetchCompanyDetail(company.company_id)}
                data-testid={`company-card-${company.company_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${company.is_archived ? 'bg-gray-200' : 'bg-[#2471A3]/10'}`}>
                        <Building2 className={`w-5 h-5 ${company.is_archived ? 'text-gray-500' : 'text-[#2471A3]'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1A2636]">{company.name}</h3>
                        {company.industry && (
                          <p className="text-sm text-gray-500">{company.industry}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {company.is_archived && (
                        <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700">
                          <Archive className="w-3 h-3 mr-1" />
                          Archived
                        </Badge>
                      )}
                      {!company.is_archived && getStatusBadge(company.status)}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <Users className="w-4 h-4" />
                      <span>{company.user_count} users</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FolderKanban className="w-4 h-4" />
                      <span>{company.project_count} projects</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      Created {new Date(company.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 px-2 ${company.is_archived ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchiveToggle(company);
                        }}
                        disabled={archiving === company.company_id}
                        data-testid={`archive-btn-${company.company_id}`}
                      >
                        {archiving === company.company_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : company.is_archived ? (
                          <ArchiveRestore className="w-4 h-4" />
                        ) : (
                          <Archive className="w-4 h-4" />
                        )}
                      </Button>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Company Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {companyDetail && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-[#2471A3]" />
                    {companyDetail.company.name}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Company Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {companyDetail.company.industry && (
                      <div>
                        <span className="text-gray-500">Industry:</span>
                        <span className="ml-2 font-medium">{companyDetail.company.industry}</span>
                      </div>
                    )}
                    {companyDetail.company.website && (
                      <div>
                        <span className="text-gray-500">Website:</span>
                        <a href={companyDetail.company.website} target="_blank" rel="noopener noreferrer" className="ml-2 text-[#2471A3] hover:underline">
                          {companyDetail.company.website}
                        </a>
                      </div>
                    )}
                    {companyDetail.company.phone && (
                      <div>
                        <span className="text-gray-500">Phone:</span>
                        <span className="ml-2 font-medium">{companyDetail.company.phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Users Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-[#1A2636]">Users ({companyDetail.users.length})</h3>
                      <AddUserDialog
                        companyId={companyDetail.company.company_id}
                        availableUsers={availableUsers.filter(
                          (u) => !companyDetail.users.find((cu) => cu.user_id === u.user_id)
                        )}
                        permissionLevels={permissionLevels}
                        onAdd={addUserToCompany}
                      />
                    </div>
                    <div className="space-y-2">
                      {companyDetail.users.map((cu) => (
                        <div
                          key={cu.user_id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-[#1A2636]">{cu.name || cu.email}</p>
                            <p className="text-sm text-gray-500">{cu.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select
                              value={cu.permission_level}
                              onValueChange={(value) =>
                                updateUserPermissions(companyDetail.company.company_id, cu.user_id, value)
                              }
                            >
                              <SelectTrigger className="w-[140px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(permissionLevels).map(([key, level]) => (
                                  <SelectItem key={key} value={key}>
                                    {level.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() =>
                                removeUserFromCompany(companyDetail.company.company_id, cu.user_id)
                              }
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                      {companyDetail.users.length === 0 && (
                        <p className="text-center text-gray-500 py-4">No users in this company</p>
                      )}
                    </div>
                  </div>

                  {/* Projects Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-[#1A2636]">
                        Projects ({companyDetail.projects.length})
                      </h3>
                      <AddProjectDialog
                        companyId={companyDetail.company.company_id}
                        companyUsers={companyDetail.users}
                        onAdd={createProjectForCompany}
                      />
                    </div>
                    <div className="space-y-2">
                      {companyDetail.projects.map((project) => {
                        const statusConfig = PROJECT_STATUS_CONFIG[project.project_status] || PROJECT_STATUS_CONFIG.not_started;
                        const StatusIcon = statusConfig.icon;
                        return (
                          <div
                            key={project.project_id}
                            className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                            onClick={() => openProjectDetail(project)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Briefcase className="w-4 h-4 text-[#2471A3]" />
                                  <p className="font-medium text-[#1A2636]">{project.name}</p>
                                </div>
                                {project.description && (
                                  <p className="text-xs text-gray-500 mb-2 line-clamp-1">{project.description}</p>
                                )}
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {project.monthly_hours} hrs/month
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <DollarSign className="w-3 h-3" />
                                    ${project.hourly_rate}/hr
                                  </span>
                                  {project.assigned_users?.length > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {project.assigned_users.length} assigned
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Project Status Badge */}
                                <Badge className={`${statusConfig.color} flex items-center gap-1`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {statusConfig.label}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteProject(project.project_id);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {companyDetail.projects.length === 0 && (
                        <div className="text-center py-6 text-gray-500">
                          <Briefcase className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                          <p>No projects yet</p>
                          <p className="text-xs mt-1">Add a project to start tracking work</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Project Detail Dialog with Status Management */}
        <Dialog open={showProjectDetailDialog} onOpenChange={setShowProjectDetailDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            {selectedProject && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-[#2471A3]" />
                    {selectedProject.name}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Project Info */}
                  {selectedProject.description && (
                    <p className="text-sm text-gray-600">{selectedProject.description}</p>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>{selectedProject.monthly_hours} hrs/month</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span>${selectedProject.hourly_rate}/hr</span>
                    </div>
                  </div>

                  {/* Status Management */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-[#1A2636] mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Project Status
                    </h4>
                    <ProjectStatusSelector
                      currentStatus={selectedProject.project_status || "not_started"}
                      onStatusChange={(newStatus, note) => updateProjectStatus(selectedProject.project_id, newStatus, note)}
                    />
                  </div>

                  {/* Status History */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-[#1A2636] mb-3">Status History</h4>
                    {projectStatusHistory.length > 0 ? (
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {projectStatusHistory.map((entry, idx) => {
                          const newStatusConfig = PROJECT_STATUS_CONFIG[entry.new_status] || PROJECT_STATUS_CONFIG.not_started;
                          return (
                            <div key={entry.history_id || idx} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <div className={`w-3 h-3 rounded-full ${newStatusConfig.color.replace('text-', 'bg-').split(' ')[0]}`} />
                                {idx < projectStatusHistory.length - 1 && (
                                  <div className="w-0.5 flex-1 bg-gray-200 mt-1" />
                                )}
                              </div>
                              <div className="flex-1 pb-3">
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="font-medium">{entry.new_status_label || newStatusConfig.label}</span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-gray-500 text-xs">
                                    {new Date(entry.changed_at).toLocaleDateString()} {new Date(entry.changed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500">by {entry.changed_by_name}</p>
                                {entry.note && (
                                  <p className="text-xs text-gray-600 mt-1 bg-gray-50 p-2 rounded">
                                    {entry.note}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">No status changes yet</p>
                    )}
                  </div>

                  {/* Assigned Clients Section */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-[#1A2636] mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Assigned Clients
                    </h4>
                    <ProjectUserAssignment
                      project={selectedProject}
                      companyUsers={companyDetail?.users || []}
                      onUpdate={(userIds) => updateProjectAssignedUsers(selectedProject.project_id, userIds)}
                    />
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

// Add User Dialog Component
const AddUserDialog = ({ companyId, availableUsers, permissionLevels, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedPermission, setSelectedPermission] = useState("standard");

  const handleAdd = () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }
    onAdd(companyId, selectedUser, selectedPermission);
    setOpen(false);
    setSelectedUser("");
    setSelectedPermission("standard");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="add-user-btn">
          <UserPlus className="w-4 h-4 mr-1" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add User to Company</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Select User</label>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a user..." />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((user) => (
                  <SelectItem key={user.user_id} value={user.user_id}>
                    {user.name || user.email} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Permission Level</label>
            <Select value={selectedPermission} onValueChange={setSelectedPermission}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(permissionLevels).map(([key, level]) => (
                  <SelectItem key={key} value={key}>
                    <div>
                      <span className="font-medium">{level.name}</span>
                      <span className="text-xs text-gray-500 ml-2">{level.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleAdd} className="w-full bg-[#2471A3] hover:bg-[#1A5276]">
            Add to Company
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Add Project Dialog Component
const AddProjectDialog = ({ companyId, companyUsers, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectData, setProjectData] = useState({
    name: "",
    description: "",
    monthly_hours: "40",
    hourly_rate: "150",
    assigned_users: [],
  });

  const handleAdd = async () => {
    if (!projectData.name.trim()) {
      toast.error("Project name is required");
      return;
    }

    setLoading(true);
    const success = await onAdd(companyId, {
      ...projectData,
      assigned_users: projectData.assigned_users.join(","),
    });
    setLoading(false);

    if (success) {
      setOpen(false);
      setProjectData({
        name: "",
        description: "",
        monthly_hours: "40",
        hourly_rate: "150",
        assigned_users: [],
      });
    }
  };

  const toggleUserAssignment = (userId) => {
    setProjectData(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-1" />
          Add Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Project to Company</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Project Name *</label>
            <Input
              value={projectData.name}
              onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
              placeholder="e.g., Website Redesign"
              data-testid="project-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <Input
              value={projectData.description}
              onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
              placeholder="Brief project description..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Monthly Hours</label>
              <Input
                type="number"
                value={projectData.monthly_hours}
                onChange={(e) => setProjectData({ ...projectData, monthly_hours: e.target.value })}
                min="1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Hourly Rate ($)</label>
              <Input
                type="number"
                value={projectData.hourly_rate}
                onChange={(e) => setProjectData({ ...projectData, hourly_rate: e.target.value })}
                min="0"
                step="0.01"
              />
            </div>
          </div>
          
          {/* Assign Users Section */}
          {companyUsers && companyUsers.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Assign Users (optional)
              </label>
              <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                {companyUsers.map((user) => (
                  <label
                    key={user.user_id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={projectData.assigned_users.includes(user.user_id)}
                      onChange={() => toggleUserAssignment(user.user_id)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{user.name || user.email}</span>
                    {user.is_company_admin && (
                      <Badge variant="secondary" className="text-[10px] px-1.5">Admin</Badge>
                    )}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {projectData.assigned_users.length} user(s) will have access to this project
              </p>
            </div>
          )}

          <Button 
            onClick={handleAdd} 
            disabled={loading}
            className="w-full bg-[#2471A3] hover:bg-[#1A5276]"
            data-testid="submit-project-btn"
          >
            {loading ? "Creating..." : "Create Project"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Project Status Selector Component with note input
const ProjectStatusSelector = ({ currentStatus, onStatusChange }) => {
  const [isChanging, setIsChanging] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const handleStatusChange = async () => {
    if (selectedStatus === currentStatus && !note.trim()) {
      setIsChanging(false);
      return;
    }

    setLoading(true);
    const success = await onStatusChange(selectedStatus, note);
    setLoading(false);

    if (success) {
      setIsChanging(false);
      setNote("");
    }
  };

  const currentConfig = PROJECT_STATUS_CONFIG[currentStatus] || PROJECT_STATUS_CONFIG.not_started;
  const CurrentIcon = currentConfig.icon;

  if (!isChanging) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className={`${currentConfig.color} flex items-center gap-1.5 px-3 py-1`}>
            <CurrentIcon className="w-4 h-4" />
            {currentConfig.label}
          </Badge>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setIsChanging(true)}
          data-testid="change-status-btn"
        >
          Change Status
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">New Status</label>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(PROJECT_STATUS_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setSelectedStatus(key)}
                className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-colors ${
                  selectedStatus === key
                    ? "border-[#2471A3] bg-[#2471A3]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Icon className={`w-4 h-4 ${selectedStatus === key ? "text-[#2471A3]" : "text-gray-500"}`} />
                <span className={`text-sm ${selectedStatus === key ? "text-[#2471A3] font-medium" : "text-gray-600"}`}>
                  {config.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">
          Note (optional)
        </label>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add context for this status change..."
          rows={2}
          className="text-sm"
        />
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setIsChanging(false);
            setSelectedStatus(currentStatus);
            setNote("");
          }}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          onClick={handleStatusChange}
          disabled={loading}
          className="flex-1 bg-[#2471A3] hover:bg-[#1A5276]"
          data-testid="confirm-status-btn"
        >
          {loading ? "Updating..." : "Update Status"}
        </Button>
      </div>
    </div>
  );
};

// Project User Assignment Component
const ProjectUserAssignment = ({ project, companyUsers, onUpdate }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Use useMemo to compute initial state from project
  const projectUsers = project.assigned_users || [];
  
  // Initialize state when project changes (controlled reset)
  const projectId = project.project_id;
  const [lastProjectId, setLastProjectId] = useState(projectId);
  
  if (projectId !== lastProjectId) {
    setSelectedUsers(projectUsers);
    setHasChanges(false);
    setLastProjectId(projectId);
  }

  // Initialize on first render
  if (selectedUsers.length === 0 && projectUsers.length > 0 && !hasChanges) {
    setSelectedUsers(projectUsers);
  }

  const toggleUser = (userId) => {
    const newSelected = selectedUsers.includes(userId)
      ? selectedUsers.filter(id => id !== userId)
      : [...selectedUsers, userId];
    setSelectedUsers(newSelected);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    const success = await onUpdate(selectedUsers);
    setLoading(false);
    if (success) {
      setHasChanges(false);
    }
  };

  if (!companyUsers || companyUsers.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">No users in this company</p>
        <p className="text-xs mt-1">Add users to the company first</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="border rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
        {companyUsers.map((user) => (
          <label
            key={user.user_id}
            className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedUsers.includes(user.user_id)}
              onChange={() => toggleUser(user.user_id)}
              className="rounded border-gray-300 w-4 h-4 text-[#2471A3] focus:ring-[#2471A3]"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A2636] truncate">
                {user.name || "Unnamed User"}
              </p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>
            {user.is_company_admin && (
              <Badge variant="secondary" className="text-[10px] px-1.5">Admin</Badge>
            )}
          </label>
        ))}
      </div>
      
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <strong>{selectedUsers.length}</strong> client(s) selected
        </p>
        {hasChanges && (
          <Button
            onClick={handleSave}
            disabled={loading}
            size="sm"
            className="bg-[#2471A3] hover:bg-[#1A5276]"
            data-testid="save-assigned-users-btn"
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AdminCompanies;
